import CloseIcon from '@mui/icons-material/Close';
import { Button, Grid, IconButton, Link, Menu, MenuItem, TextField, Theme, Typography, Autocomplete, Tabs, Tab, Pagination, PaginationItem, Divider } from '@mui/material';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import OZCheckbox from '../OZCheckbox/OZCheckbox';
import '../FilterDrawer/FilterDrawer.css';
import { getFilters, getCustomTags } from '../../services/api-service';
import { ExpandMore, Add, Delete } from '@mui/icons-material';
import { showErrorNotification } from '../admin/common/NotiService';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { PaginationRenderIcon } from '../admin/common/OZDataGrid/OZGridPagination';

const filterTypes = [
    { id: 1, label: 'Campaign', value: 'Campaign' },
    { id: 2, label: 'Skill', value: 'Skill' },
    { id: 3, label: 'Group', value: 'Group' },
    { id: 4, label: 'Skill Location', value: 'Location' },
    { id: 5, label: 'Skill Details', value: 'Detail' },
    { id: 6, label: 'Skill Details2', value: 'Detail2' },
    { id: 7, label: 'Skill Details3', value: 'Detail3' },
];

// Common props for Autocomplete components
const commonAutocompleteProps = {
    placeholder: "Select",
    size: 'small' as 'small',
    limitTags: 2,
    isOptionEqualToValue: (option: any, value: any) => {
        if (typeof option === 'string' && typeof value === 'string') {
            return option === value;
        }
        return option?.id === value?.id; // Compare by ID for FilterSection objects
    },
    clearIcon: null,
    disableCloseOnSelect: true,
    componentsProps: { paper: { sx: { border: "1px solid #D6D6D7", marginTop: "6px", borderRadius: "8px" } } },
    renderOption: (props: any, option: any, { selected }: any) => (
        <Box {...props} sx={{
            paddingLeft: "10px !important",
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            height: '24px !important',
            overflow: 'hidden',
        }}>
            <Typography textOverflow={"ellipsis"} overflow={"hidden"} whiteSpace={"nowrap"} width={"100%"} fontSize={"12px"} fontWeight={400} color={"#3D4C5E"} textTransform={"capitalize"}>
                {option.label || option} {/* Handle both object and string options if necessary */}
            </Typography>
        </Box>
    ),
    renderInput: (params: any) => <TextField placeholder='Select' size='small'
        sx={{
            '& .MuiOutlinedInput-root': {
                fontSize: "14px",
                color: "#3D4C5E",
                borderRadius: '8px',
                minHeight: "36px"
            },
            minHeight: "36px"
        }}
        {...params}
        fullWidth
    />,
};

const useStyles = makeStyles((theme: Theme) => ({
    filterContainer: {
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column'
    },
    filterHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    headerText: {
        fontWeight: 600,
        fontSize: '18px',
        lineHeight: '21px',
        color: '#212121'
    },
    drawerGutter: {
        paddingTop: '74px'
    },
    caseTab: {
        border: '1px solid #E0E0E0',
        borderRadius: '8px',
        mx: 0.5,
        marginBottom: '8px',
        '&.Mui-selected': {
            fontWeight: 600,
        },
        textTransform: 'none',
        minHeight: 'unset',
        padding: '6px 12px',
        fontSize: '13px'
    },
    conditionBox: {
        border: '1px solid #eee',
        borderRadius: '8px',
        padding: '10px',
        marginBottom: '10px',
        position: 'relative'
    },
    addCaseButton: {
        marginTop: '10px',
        marginBottom: '10px'
    },
}));

const CONDITION_OPTIONS = ['AND', 'OR'];
const MAX_CASES = 3;
const MAX_CONDITIONS_PER_CASE = 2;
const ITEMS_PER_PAGE = 60;

// Interfaces
interface SelectedFilter {
    id: string;
    label: string;
}

interface FilterSection {
    id: number;
    label: string;
    value: string;
}

interface ConditionFilter {
    filterType: string;
    filterSection: FilterSection | null;
    selectedFilters: SelectedFilter[];
}

interface CaseFilter {
    condition: 'AND' | 'OR';
    conditionFilters: ConditionFilter[];
}

interface FilterDrawerProps {
    toggleDrawer: (open: boolean) => void;
    isOpen: boolean | undefined;
    applyFilters?: (filters: { caseCondition: 'AND' | 'OR'; caseFilters: CaseFilter[] }) => void;
    existingCaseFilters?: { caseCondition: 'AND' | 'OR'; caseFilters: CaseFilter[] };
    allowedFilters?: string[];
    dynamicFilters?: boolean;
    useTagFilters?: boolean;
}

// ----- Standalone ConditionContent Component -----
interface ConditionContentProps {
    caseIdx: number;
    conditionIdx: number;
    condition: ConditionFilter;
    availableFilterTypes: FilterSection[];
    filterCache: { [key: string]: any[] };
    loadingStates: { [key: string]: boolean };
    onFilterTypeChange: (caseIdx: number, conditionIdx: number, selectedType: FilterSection | null) => void;
    onCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>, item: any, caseIdx: number, conditionIdx: number) => void;
    onSelectAll: (caseIdx: number, conditionIdx: number) => void;
    onClearAll: (caseIdx: number, conditionIdx: number) => void;
    onRemoveCondition: (caseIdx: number, conditionIdx: number) => void;
    totalConditionsInCase: number;
    totalConditionsOverall: number;
    isOnlyCase: boolean;
}

const ConditionContent: React.FC<ConditionContentProps> = React.memo(({
    caseIdx,
    conditionIdx,
    condition,
    availableFilterTypes,
    filterCache,
    loadingStates,
    onFilterTypeChange,
    onCheckboxChange,
    onSelectAll,
    onClearAll,
    onRemoveCondition,
    totalConditionsInCase,
    totalConditionsOverall,
    isOnlyCase
}) => {
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const filterSectionValue = condition.filterSection?.value;
    const isLoading = filterSectionValue ? loadingStates[filterSectionValue] : false;

    // Reset local state when filter type changes
    useEffect(() => {
        setSearchText('');
        setCurrentPage(1);
    }, [condition.filterSection?.id]);

    const allItems = useMemo(() => {
        return filterSectionValue ? (filterCache[filterSectionValue] || []) : [];
    }, [filterSectionValue, filterCache]);

    const filteredItems = useMemo(() => {
        return searchText.length
            ? allItems.filter((item: any) => item.Name && item.Name.toLowerCase().includes(searchText.toLowerCase()))
            : allItems;
    }, [allItems, searchText]);

    const totalItems = filteredItems.length;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const displayItems = filteredItems.slice(startIndex, endIndex);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchText(event.target.value);
        setCurrentPage(1);
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value);
    };

    const handleSelectAllClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onSelectAll(caseIdx, conditionIdx);
    };

    const handleClearAllClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClearAll(caseIdx, conditionIdx);
    };

    // const handleRemoveClick = (e: React.MouseEvent) => {
    //     e.stopPropagation();
    //     onRemoveCondition(caseIdx, conditionIdx);
    // };

    const isChecked = (item: any): boolean => {
        return condition.selectedFilters.some(sf => sf.id === String(item.Id));
    };

    const selectedValue = availableFilterTypes.find(ft => ft.id === condition.filterSection?.id) || null;

    const filterItemListElements = displayItems?.map((item: any, index: number) => (
        <Grid key={`${item.Id}-${index}-case${caseIdx}-cond${conditionIdx}`} item xs={6} className="filter-checkbox-col">
            <OZCheckbox
                checked={isChecked(item)}
                label={item.Name}
                id={`${String(item.Id)}-case${caseIdx}-cond${conditionIdx}`}
                onChange={(e: any) => onCheckboxChange(e, item, caseIdx, conditionIdx)}
            />
        </Grid>
    ));

    return (
        <Box>


            <Grid container spacing={1}>
                <Grid item xs={condition.filterSection ? 6 : 12}>
                    <Typography fontSize={12} fontWeight={400} color={'#666'} mb={0.3}>
                        Filter Type
                    </Typography>
                    <Autocomplete
                        {...commonAutocompleteProps}
                        options={availableFilterTypes}
                        value={selectedValue}
                        popupIcon={<ExpandMore />}
                        getOptionLabel={(option) => option.label}
                        onChange={(_, newValue) => onFilterTypeChange(caseIdx, conditionIdx, newValue)}
                        renderInput={(params) => <TextField {...params} placeholder="Select Filter Type" size="small" sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: "14px", color: "#3D4C5E", borderRadius: '8px', minHeight: "36px" }, minHeight: "36px" }} />}
                    />
                </Grid>
                {condition.filterSection && <Grid item xs={6}>
                    <Typography fontSize={12} fontWeight={400} color={'#666'} mb={0.3}>
                        Search
                    </Typography>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={`Search ${condition.filterSection.label}`}
                        value={searchText}
                        onChange={handleSearchChange}
                        sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: "14px", color: "#3D4C5E", borderRadius: '8px', minHeight: "36px" }, minHeight: "36px" }}
                    />
                </Grid>}
            </Grid >
            {
                condition.filterSection && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div className="filter-select-links filter-select-links-case-wise">
                                <Link onClick={handleSelectAllClick} underline="hover"> Select All </Link>
                                <Link onClick={handleClearAllClick} underline="hover"> Clear All </Link>
                            </div>
                            <Box sx={{ display: 'flex', mb: 1, gap: 1 }}>
                                <Typography fontSize={12} fontWeight={500} color={'#212121'}>
                                    Total:
                                </Typography>
                                <Typography fontSize={12} fontWeight={400} color={'#212121'}>
                                    {totalItems}
                                </Typography>
                                <Typography fontSize={12} fontWeight={500} color={'#212121'}>
                                    Selected Count:
                                </Typography>
                                <Typography fontSize={12} fontWeight={400} color={'#212121'}>
                                    {condition.selectedFilters.length}
                                </Typography>
                            </Box>
                        </Box>
                        <Box className="oz-filter-items-container" sx={{ maxHeight: '200px', overflowY: 'auto', height: 'auto', border: '1px solid #eee', padding: '12px', borderRadius: '8px' }}>
                            {isLoading && <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center', mt: 2 }}>...Loading {condition.filterSection.label}...</Typography>}
                            {!isLoading && filteredItems.length === 0 && <Typography sx={{ fontSize: 12, color: 'text.secondary', textAlign: 'center', mt: 2 }}>No {condition.filterSection.label} found{searchText ? ' matching search' : ''}.</Typography>}
                            {!isLoading && filteredItems.length > 0 && (
                                <Grid container spacing={1}>
                                    {filterItemListElements}
                                </Grid>
                            )}
                        </Box>
                        {!isLoading && totalItems > ITEMS_PER_PAGE && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                                <Pagination
                                    sx={{
                                        '& .MuiPaginationItem-previousNext': { color: '#3D8BF8', border: 'none', width: '20px !important' },
                                        '& .MuiPaginationItem-previousNext.Mui-disabled': { color: '#7A7A7A' },
                                        '& .MuiButtonBase-root.MuiPaginationItem-root.MuiPaginationItem-page': {
                                            backgroundColor: 'none',
                                            border: 'none',
                                            width: '16px !important',
                                            height: '24px !important'
                                        },
                                        '& .MuiButtonBase-root.MuiPaginationItem-root.Mui-selected': {
                                            backgroundColor: '#E6F4FF !important'
                                        }
                                    }}
                                    count={totalPages}
                                    page={currentPage}
                                    onChange={handlePageChange}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    shape="circular"
                                    renderItem={(item) => <PaginationRenderIcon item={item} />}
                                />
                            </Box>
                        )}
                    </>
                )
            }
        </Box >
    );
});

// ----- End Standalone ConditionContent Component -----

const FilterDrawerCaseWise: React.FC<FilterDrawerProps> = ({
    isOpen,
    toggleDrawer,
    applyFilters = () => { },
    existingCaseFilters,
    allowedFilters,
    dynamicFilters = false,
    useTagFilters = false
}) => {
    const classes = useStyles();
    const ALLOWED_FILTER_TYPES = useMemo(() => filterTypes.filter(ft => allowedFilters?.includes(ft.value)), [allowedFilters]);

    const [caseCondition, setCaseCondition] = useState<'AND' | 'OR'>(existingCaseFilters?.caseCondition || 'AND');
    const [caseFilters, setCaseFilters] = useState<CaseFilter[]>(
        existingCaseFilters?.caseFilters && existingCaseFilters.caseFilters.length > 0
            ? existingCaseFilters.caseFilters
            : [{ condition: 'AND', conditionFilters: [{ filterType: '', filterSection: null, selectedFilters: [] }] }]
    );
    const [activeCaseIndex, setActiveCaseIndex] = useState<number>(0);
    const [filterCache, setFilterCache] = useState<{ [key: string]: any[] }>({});
    const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});
    const [caseConditionMenuAnchorEl, setCaseConditionMenuAnchorEl] = useState<null | HTMLElement>(null);

    // Dynamic filter states
    const [dynamicFilterTypes, setDynamicFilterTypes] = useState<FilterSection[]>([]);
    const [loadingDynamicFilters, setLoadingDynamicFilters] = useState(false);

    const activeCase = caseFilters[activeCaseIndex];
    const totalConditionsOverall = useMemo(() => caseFilters.reduce((sum, c) => sum + c.conditionFilters.length, 0), [caseFilters]);
    const caseConditionMenuOpen = Boolean(caseConditionMenuAnchorEl);

    console.log("caseFilters", caseFilters);

    // Function to get all custom tag list and transform to required format
    const getAllCustomTagList = useCallback(async () => {
        if (!dynamicFilters || !useTagFilters) return [];

        setLoadingDynamicFilters(true);
        try {
            const resp: any = await getCustomTags();
            if (resp.Status === "Success" && resp.Data && Array.isArray(resp.Data) && resp.Data.length > 0) {
                const customTagList = resp.Data.reduce((acc: any[], item: any) => {
                    if (!item.isActive) {
                        return acc;
                    }
                    try {
                        if (item && typeof item === "object" && "tagValue" in item && item.tagValue && typeof item.tagValue === "string" && item.tagValue.length > 0) {
                            item.tagValue = JSON.parse(item.tagValue)[0][item.tagName] || [];
                        } else {
                            item.tagValue = [];
                        }
                    } catch (e) {
                        console.log("Error in custom tag list", e);
                        item.tagValue = [];
                    }
                    acc.push({ tagName: item.tagName, tagValue: item.tagValue });
                    return acc;
                }, []);

                // Transform to FilterSection format and populate filterCache
                const dynamicFilterSections: FilterSection[] = customTagList.map((tag: any, index: number) => ({
                    id: tag.tagName, // Use a higher ID to avoid conflicts with static filters
                    label: tag.tagName,
                    value: tag.tagName
                }));

                // Populate filterCache with tag values
                const newFilterCache: { [key: string]: any[] } = {};
                customTagList.forEach((tag: any) => {
                    if (tag.tagValue && Array.isArray(tag.tagValue)) {
                        newFilterCache[tag.tagName] = tag.tagValue.map((value: string, index: number) => ({
                            Id: value, // Use index-based ID
                            Name: value
                        }));
                    }
                });

                setFilterCache(prev => ({ ...prev, ...newFilterCache }));
                return dynamicFilterSections;
            }
            return [];
        } catch (error) {
            console.error("Error fetching custom tags:", error);
            showErrorNotification("Failed to fetch custom tags.");
            return [];
        } finally {
            setLoadingDynamicFilters(false);
        }
    }, [dynamicFilters, useTagFilters]);

    // Load dynamic filters on mount if conditions are met
    useEffect(() => {
        if (dynamicFilters && useTagFilters && isOpen) {
            getAllCustomTagList().then(setDynamicFilterTypes);
        }
    }, [dynamicFilters, useTagFilters, isOpen, getAllCustomTagList]);

    useEffect(() => {
        try {
            if (caseFilters.length > 0) {
                const currentAllowedFilters = dynamicFilters && useTagFilters ?
                    dynamicFilterTypes.map(ft => ft.value) :
                    allowedFilters;

                if (currentAllowedFilters && currentAllowedFilters.length > 0) {
                    setCaseFilters(prev => {
                        const cases = [...prev];
                        cases.forEach(c => {
                            c.conditionFilters = c.conditionFilters.filter(cond => currentAllowedFilters?.includes(cond.filterSection?.value || ''));
                        });
                        if (cases.every(c => c.conditionFilters.length === 0)) {
                            cases[0].conditionFilters = [{ filterType: '', filterSection: null, selectedFilters: [] }];
                        }
                        return cases;
                    })
                }
            }
        } catch (error) {
            console.error("Error in FilterDrawerCaseWise", error);
        }
    }, [dynamicFilters, useTagFilters, dynamicFilterTypes, allowedFilters])

    const getAvailableFilterTypesForCondition = useCallback((): FilterSection[] => {
        return dynamicFilters && useTagFilters ? dynamicFilterTypes : ALLOWED_FILTER_TYPES;
    }, [dynamicFilters, useTagFilters, dynamicFilterTypes, ALLOWED_FILTER_TYPES]);

    const getAvailableFilterTypesForNewCondition = useCallback((): FilterSection[] => {
        return dynamicFilters && useTagFilters ? dynamicFilterTypes : ALLOWED_FILTER_TYPES;
    }, [dynamicFilters, useTagFilters, dynamicFilterTypes, ALLOWED_FILTER_TYPES]);

    const fetchFiltersIfNeeded = useCallback(async (filterSectionValue: string | undefined) => {
        if (!filterSectionValue) return;

        // For dynamic filters, data is already loaded via getAllCustomTagList
        if (dynamicFilters && useTagFilters) {
            return;
        }

        // For static filters, use existing logic
        if (filterCache[filterSectionValue] || loadingStates[filterSectionValue]) {
            return;
        }
        setLoadingStates(prev => ({ ...prev, [filterSectionValue]: true }));
        try {
            const response = await getFilters(filterSectionValue, {});
            setFilterCache(prev => ({ ...prev, [filterSectionValue]: response }));
        } catch (error) {
            console.error(`Error fetching ${filterSectionValue} filters:`, error);
            showErrorNotification(`Failed to fetch ${filterSectionValue} filters.`);
            setFilterCache(prev => ({ ...prev, [filterSectionValue]: [] }));
        } finally {
            setLoadingStates(prev => ({ ...prev, [filterSectionValue]: false }));
        }
    }, [dynamicFilters, useTagFilters, filterCache, loadingStates]);

    useEffect(() => {
        if (isOpen) {
            caseFilters.forEach(c => {
                c.conditionFilters.forEach(cond => {
                    if (cond.filterSection?.value) {
                        fetchFiltersIfNeeded(cond.filterSection.value);
                    }
                });
            });
        }
    }, [isOpen, caseFilters, fetchFiltersIfNeeded]);

    const handleCaseConditionMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setCaseConditionMenuAnchorEl(event.currentTarget);
    };

    const handleCaseConditionMenuClose = (selectedCondition?: 'AND' | 'OR') => {
        if (selectedCondition) {
            setCaseCondition(selectedCondition);
        }
        setCaseConditionMenuAnchorEl(null);
    };

    const handleCaseTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setActiveCaseIndex(newValue);
    };

    const addCase = () => {
        if (caseFilters.length < MAX_CASES) {
            setCaseFilters(prev => [...prev, { condition: 'AND', conditionFilters: [{ filterType: '', filterSection: null, selectedFilters: [] }] }]);
            setActiveCaseIndex(caseFilters.length);
        } else {
            showErrorNotification(`Maximum ${MAX_CASES} cases allowed.`);
        }
    };

    const removeCase = (indexToRemove: number) => {
        if (caseFilters.length <= 1) {
            showErrorNotification("At least one case is required.");
            return;
        }
        const newCaseFilters = caseFilters.filter((_, index) => index !== indexToRemove);
        setCaseFilters(newCaseFilters);
        if (activeCaseIndex >= indexToRemove) {
            const newIndex = Math.max(0, activeCaseIndex - 1);
            setActiveCaseIndex(newIndex);
        }
    };

    const handleConditionLogicChange = (caseIdx: number, selectedCondition: 'AND' | 'OR') => {
        setCaseFilters(prev => prev.map((c, idx) => idx === caseIdx ? { ...c, condition: selectedCondition } : c));
    };

    const addCondition = (caseIdx: number) => {
        const currentCase = caseFilters[caseIdx];
        if (currentCase && currentCase.conditionFilters.length < MAX_CONDITIONS_PER_CASE) {
            const availableTypes = getAvailableFilterTypesForNewCondition();
            const initialFilterSection = availableTypes.length > 0 ? availableTypes[0] : null;
            const newCondition: ConditionFilter = {
                filterType: initialFilterSection ? String(initialFilterSection.id) : '',
                filterSection: initialFilterSection,
                selectedFilters: []
            };
            setCaseFilters(prev => prev.map((c, idx) => idx === caseIdx ? { ...c, conditionFilters: [...c.conditionFilters, newCondition] } : c));
            if (initialFilterSection) {
                fetchFiltersIfNeeded(initialFilterSection.value);
            }
        } else {
            showErrorNotification(`Maximum ${MAX_CONDITIONS_PER_CASE} conditions allowed per case.`);
        }
    };

    const removeCondition = (caseIdx: number, conditionIdx: number) => {
        const currentCase = caseFilters[caseIdx];
        if (totalConditionsOverall <= 1) {
            showErrorNotification("At least one condition is required overall.");
            return;
        }
        if (currentCase && currentCase.conditionFilters.length <= 1 && caseFilters.length <= 1) {
            showErrorNotification("At least one condition is required per case if it's the only case.");
            return;
        }
        setCaseFilters(prev => prev.map((c, cIdx) =>
            cIdx === caseIdx
                ? { ...c, conditionFilters: c.conditionFilters.filter((_, condIdx) => condIdx !== conditionIdx) }
                : c
        ));
    };

    const handleFilterTypeChange = (caseIdx: number, conditionIdx: number, selectedType: FilterSection | null) => {
        setCaseFilters(prev => prev.map((c, cIdx) =>
            cIdx === caseIdx
                ? {
                    ...c,
                    conditionFilters: c.conditionFilters.map((cond, condIdxMap) =>
                        condIdxMap === conditionIdx
                            ? { ...cond, filterSection: selectedType, filterType: selectedType ? String(selectedType.id) : '', selectedFilters: [] }
                            : cond
                    )
                }
                : c
        ));
        if (selectedType) {
            fetchFiltersIfNeeded(selectedType.value);
        }
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, item: any, caseIdx: number, conditionIdx: number) => {
        const { checked } = event.target;
        const itemId = item.Id;
        const itemLabel = item.Name;

        // Make a deep copy to ensure we don't have reference issues
        setCaseFilters(prev => {
            const updatedFilters = JSON.parse(JSON.stringify(prev));

            // Find the exact case and condition to update
            const targetCase = updatedFilters[caseIdx];
            if (targetCase) {
                const targetCondition = targetCase.conditionFilters[conditionIdx];
                if (targetCondition) {
                    // Check max limit for Campaign filter type
                    if (checked &&
                        targetCondition.filterSection?.value === 'Campaign' &&
                        targetCondition.selectedFilters.length >= 50) {
                        showErrorNotification('Maximum 50 Campaigns can be selected at a time.');
                        return prev; // Return previous state without changes
                    }

                    if (checked) {
                        // Add the item if not already present
                        if (!targetCondition.selectedFilters.some((sf: any) => sf.id === String(itemId))) {
                            targetCondition.selectedFilters.push({ id: String(itemId), label: itemLabel });
                        }
                    } else {
                        // Remove the item
                        targetCondition.selectedFilters = targetCondition.selectedFilters.filter(
                            (sf: any) => sf.id !== String(itemId)
                        );
                    }
                }
            }

            return updatedFilters;
        });
    };

    const getAllItemsForCondition = useCallback((caseIdx: number, conditionIdx: number): any[] => {
        const filterSectionValue = caseFilters[caseIdx]?.conditionFilters[conditionIdx]?.filterSection?.value;
        return filterSectionValue ? filterCache[filterSectionValue] || [] : [];
    }, [caseFilters, filterCache]);

    const selectAll = (caseIdx: number, conditionIdx: number) => {
        const condition = caseFilters[caseIdx]?.conditionFilters[conditionIdx];
        const allItems = getAllItemsForCondition(caseIdx, conditionIdx);
        if (!allItems || allItems.length === 0) return;

        // Get the search text from the DOM since we don't have direct access to the ConditionContent state
        const searchTextFieldElement = document.querySelector(`input[placeholder="Search ${condition?.filterSection?.label}"]`) as HTMLInputElement;
        const searchText = searchTextFieldElement?.value || '';

        // Filter items based on search text if present
        const itemsToSelect = searchText.length > 0
            ? allItems.filter((item: any) => item.Name && item.Name.toLowerCase().includes(searchText.toLowerCase()))
            : allItems;

        // Check if Campaign filter type and limit to 50 items if needed
        if (condition?.filterSection?.value === 'Campaign' && itemsToSelect.length > 50) {
            showErrorNotification('Maximum 50 Campaigns can be selected at a time. Only the first 50 will be selected.');
            const newSelectedFilters = itemsToSelect.slice(0, 50).map((item: any) => ({ id: String(item.Id), label: item.Name }));

            setCaseFilters(prev => prev.map((c, cIdxMap) =>
                cIdxMap === caseIdx
                    ? {
                        ...c,
                        conditionFilters: c.conditionFilters.map((cond, condIdxMap) =>
                            condIdxMap === conditionIdx
                                ? { ...cond, selectedFilters: newSelectedFilters }
                                : cond
                        )
                    }
                    : c
            ));
            return;
        }

        const newSelectedFilters = itemsToSelect.map((item: any) => ({ id: String(item.Id), label: item.Name }));

        setCaseFilters(prev => prev.map((c, cIdxMap) =>
            cIdxMap === caseIdx
                ? {
                    ...c,
                    conditionFilters: c.conditionFilters.map((cond, condIdxMap) =>
                        condIdxMap === conditionIdx
                            ? { ...cond, selectedFilters: newSelectedFilters }
                            : cond
                    )
                }
                : c
        ));
    };

    const clearAll = (caseIdx: number, conditionIdx: number) => {
        setCaseFilters(prev => prev.map((c, cIdxMap) =>
            cIdxMap === caseIdx
                ? {
                    ...c,
                    conditionFilters: c.conditionFilters.map((cond, condIdxMap) =>
                        condIdxMap === conditionIdx
                            ? { ...cond, selectedFilters: [] }
                            : cond
                    )
                }
                : c
        ));
    };

    const handleApplyFilters = () => {
        try {
            for (let i = 0; i < caseFilters.length; i++) {
                const caseFilter = caseFilters[i];
                if (caseFilter.conditionFilters.length === 0) {
                    showErrorNotification(`Case ${i + 1} must have at least one condition defined.`);
                    setActiveCaseIndex(i);
                    return;
                }
                for (let j = 0; j < caseFilter.conditionFilters.length; j++) {
                    const conditionFilter = caseFilter.conditionFilters[j];
                    if (!conditionFilter.filterSection || !conditionFilter.filterType) {
                        showErrorNotification(`Please select a filter type for Condition ${j + 1} in Case ${i + 1}.`);
                        setActiveCaseIndex(i);
                        return;
                    }
                    if (conditionFilter.selectedFilters.length === 0 && !(caseFilters?.length === 1 && caseFilter?.conditionFilters?.length === 1)) {
                        showErrorNotification(`Please select at least one item for ${conditionFilter.filterSection.label} in Condition ${j + 1}, Case ${i + 1}.`);
                        setActiveCaseIndex(i);
                        return;
                    }
                }
            }
            if (caseFilters.some(cf => cf.conditionFilters.length === 0)) {
                showErrorNotification("All cases must have at least one condition defined.");
                return;
            }
            applyFilters({ caseCondition, caseFilters });
        } catch (e) {
            console.log("error in handleApplyFilters", e)
        }
    };

    return (
        <Drawer open={isOpen} anchor={'right'} onClose={() => toggleDrawer(false)}>
            <Box sx={{ width: 550 }} className={classes.filterContainer}>
                <div className={classes.drawerGutter}>
                    {/* Header */}
                    <div className={classes.filterHeader}>
                        <span className={classes.headerText}>Case Wise Filters</span>
                        <IconButton aria-label="close" size="small" onClick={() => toggleDrawer(false)}>
                            <CloseIcon style={{ fontSize: '22px', color: '#99a0a8' }} />
                        </IconButton>
                    </div>

                    {/* Case Condition Selector */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, mt: 1 }}>
                        <Typography fontSize={14} fontWeight={500} color={'#333'} mr={1}>
                            Cases
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <Typography fontSize={13} fontWeight={500} color={'#333'} mr={1}>
                                Combine Cases using:
                            </Typography>
                            <Box
                                sx={{
                                    border: '1px solid #D6D6D7',
                                    borderRadius: '8px',
                                    padding: '5px 10px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    height: '36px !important',
                                    minWidth: '80px',
                                    justifyContent: 'space-between'
                                }}
                                onClick={handleCaseConditionMenuOpen}
                            >
                                <Typography fontSize={14} fontWeight={500} color={'#212121'}>
                                    {caseCondition}
                                </Typography>
                                <ExpandMore sx={{ fontSize: 18, marginLeft: '2px' }} />
                            </Box>
                            <Menu
                                anchorEl={caseConditionMenuAnchorEl}
                                open={caseConditionMenuOpen}
                                onClose={() => handleCaseConditionMenuClose()}
                            >
                                {CONDITION_OPTIONS.map((option) => (
                                    <MenuItem
                                        key={option}
                                        onClick={() => handleCaseConditionMenuClose(option as 'AND' | 'OR')}
                                        selected={caseCondition === option}
                                    >
                                        {option}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    </Box>
                    {/* Case Tabs */}
                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-start' }}> {/* Align items start */}
                        <Tabs
                            value={activeCaseIndex}
                            onChange={handleCaseTabChange}
                            variant="fullWidth"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            aria-label="case filter tabs"
                            sx={{
                                flexGrow: 1,
                                minHeight: '38px', // Ensure minimum height
                                '& .MuiTabs-flexContainer': {
                                    alignItems: 'flex-start',
                                },
                                '& .MuiTab-root': {
                                    border: '1px solid #E0E0E0',
                                    borderRadius: '8px',
                                    mx: 0.5,
                                    marginBottom: '8px',
                                    textTransform: 'none',
                                    minHeight: 'unset',
                                    height: '36px',
                                    padding: '6px 12px',
                                    fontSize: '14px',
                                    // minWidth: '100px',
                                },
                                '& .Mui-selected': {
                                    fontWeight: 500,
                                    borderColor: (theme: Theme) => theme.palette.primary.main,
                                    backgroundColor: (theme: Theme) => `${theme.palette.primary.main}10`,
                                }
                            }}
                            TabIndicatorProps={{
                                style: { display: 'none' }
                            }}
                        >
                            {caseFilters.map((_, index) => (
                                <Tab
                                    key={index}
                                    label={(
                                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                                            Case {index + 1}
                                            {caseFilters.length > 1 && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); removeCase(index); }}
                                                    sx={{ ml: 1, padding: '2px' }}
                                                >
                                                    <CloseIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            )}
                                        </Box>
                                    )}
                                />
                            ))}
                        </Tabs>
                        {caseFilters.length < MAX_CASES && (
                            <Button
                                size="small"
                                startIcon={<Add />}
                                onClick={addCase}
                                variant='outlined'
                                sx={{
                                    ml: 1,
                                    textTransform: 'none',
                                    height: '36px',
                                    borderRadius: '8px',
                                    flexShrink: 0,
                                    // alignSelf: 'flex-start' // Removed, should align with Tabs container
                                }}
                            >
                                Add Case
                            </Button>
                        )}
                    </Box>
                </div>
                <Divider sx={{ mt: 1, mb: 1 }} />
                {/* Active Case Content */}
                <Box className="oz-filter-items-container" sx={{ height: 'calc(100vh - 315px)', overflowY: 'auto', paddingRight: '5px' }}>
                    {activeCase && (
                        <>
                            {activeCase.conditionFilters.length > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography fontSize={14} fontWeight={500} color={'#333'} mr={1}>
                                        Conditions
                                    </Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography fontSize={13} fontWeight={500} color={'#333'} mr={1}>
                                                Combine Conditions using:
                                            </Typography>
                                            <Autocomplete
                                                disableClearable
                                                size="small"
                                                options={CONDITION_OPTIONS}
                                                value={activeCase.condition}
                                                onChange={(_, newValue) => handleConditionLogicChange(activeCaseIndex, newValue as 'AND' | 'OR')}
                                                sx={{ width: '100px' }}
                                                renderInput={(params) => <TextField {...params} sx={{
                                                    '& .MuiOutlinedInput-root': { fontSize: "14px", borderRadius: '8px', minHeight: "36px" }, minHeight: "36px"
                                                }} />}
                                                componentsProps={{ paper: { sx: { border: "1px solid #D6D6D7", marginTop: "6px", borderRadius: "8px" } } }}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            {/* Render Conditions using Accordions */}
                            {activeCase.conditionFilters.map((condition, conditionIdx) => {
                                const availableFilterTypes = getAvailableFilterTypesForCondition();
                                const totalConditionsInCase = activeCase.conditionFilters.length;
                                const isOnlyCase = caseFilters.length === 1;
                                return (
                                    <React.Fragment key={conditionIdx}>
                                        <Accordion
                                            defaultExpanded
                                            sx={{
                                                '&.MuiAccordion-root:before': {
                                                    display: 'none'
                                                },
                                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                                                border: '1px solid #eee',
                                                borderRadius: '8px !important',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMore />}
                                                sx={{
                                                    backgroundColor: '#f5f5f5',
                                                    minHeight: '48px !important',
                                                    '& .MuiAccordionSummary-content': {
                                                        margin: '8px 0 !important',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }
                                                }}
                                            >
                                                <Typography fontWeight={500}>Condition {conditionIdx + 1}</Typography>
                                                {(totalConditionsInCase > 1 || !isOnlyCase) && totalConditionsOverall > 1 && (
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            removeCondition(activeCaseIndex, conditionIdx);
                                                        }}
                                                        sx={{ ml: 1 }}
                                                    >
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <ConditionContent
                                                    caseIdx={activeCaseIndex}
                                                    conditionIdx={conditionIdx}
                                                    condition={condition}
                                                    availableFilterTypes={availableFilterTypes}
                                                    filterCache={filterCache}
                                                    loadingStates={loadingStates}
                                                    onFilterTypeChange={handleFilterTypeChange}
                                                    onCheckboxChange={handleCheckboxChange}
                                                    onSelectAll={selectAll}
                                                    onClearAll={clearAll}
                                                    onRemoveCondition={removeCondition}
                                                    totalConditionsInCase={totalConditionsInCase}
                                                    totalConditionsOverall={totalConditionsOverall}
                                                    isOnlyCase={isOnlyCase}
                                                />
                                            </AccordionDetails>
                                        </Accordion>
                                        {totalConditionsInCase > 1 && conditionIdx < totalConditionsInCase - 1 && (
                                            <Typography align='center' sx={{ fontWeight: 500, my: 1, color: '#666', fontSize: '12px', borderBottom: '1px dashed #ccc', lineHeight: '0.1em', margin: '15px 0' }}>
                                                <span style={{ background: '#fff', padding: '0 10px' }}>
                                                    {activeCase.condition}
                                                </span>
                                            </Typography>
                                        )}
                                    </React.Fragment>
                                );
                            })}

                            {activeCase.conditionFilters.length < MAX_CONDITIONS_PER_CASE && (
                                <Button
                                    startIcon={<Add />}
                                    onClick={() => addCondition(activeCaseIndex)}
                                    variant='outlined'
                                    fullWidth
                                    sx={{
                                        textTransform: 'none',
                                        height: '36px',
                                        borderRadius: '8px',
                                        flexShrink: 0,
                                        mt: 2.5
                                    }}
                                >
                                    Add Condition
                                </Button>
                            )}
                        </>
                    )}
                    {!activeCase && caseFilters.length > 0 && (
                        <Typography sx={{ textAlign: 'center', mt: 3, color: 'text.secondary' }}>
                            Select a case tab to view or edit conditions.
                        </Typography>
                    )}
                    {caseFilters.length === 0 && (
                        <Typography sx={{ textAlign: 'center', mt: 3, color: 'text.secondary' }}>
                            Click "Add Case" to start defining filters.
                        </Typography>
                    )}
                </Box>

                {/* Footer */}
                <div className="oz-filter-items-footer">
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handleApplyFilters}
                        disabled={
                            loadingDynamicFilters ||
                            caseFilters.length === 0 ||
                            caseFilters.some(c => c.conditionFilters.length === 0)
                        }
                    >
                        {loadingDynamicFilters ? 'Loading Filters...' : 'Apply Filter'}
                    </Button>
                </div>
            </Box>
        </Drawer>
    );
};

export default React.memo(FilterDrawerCaseWise);
